// The patient's screen, opened by the operator console. It goes fullscreen on the chosen
// monitor, runs whatever test the console asks for, and reports progress back.

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type SessionInfo } from "../api";
import { Stage } from "../components/Stage";
import { screenDetails } from "../lib/probe";
import { sessionChannel, type ConsoleToDisplay, type DisplayToConsole } from "../runner/channel";
import { EYE_INSTRUCTION, testTitle, useRunner } from "../runner/useRunner";

interface ScreenOption { label: string; screen: unknown }

export function DisplayWindow() {
  const sessionId = Number(useParams().sessionId);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [screens, setScreens] = useState<ScreenOption[]>([]);
  const [chosen, setChosen] = useState(0);
  const [message, setMessage] = useState<string>("Waiting for the operator…");
  const r = useRunner(session);
  const chan = useRef<BroadcastChannel | null>(null);
  const runRef = useRef(r.run);
  runRef.current = r.run;

  useEffect(() => { api.session(sessionId).then(setSession, (e) => setMessage(String(e))); }, [sessionId]);

  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  useEffect(() => {
    const c = sessionChannel(sessionId);
    chan.current = c;
    const post = (m: DisplayToConsole) => c.postMessage(m);
    c.onmessage = async (ev: MessageEvent<ConsoleToDisplay>) => {
      const m = ev.data;
      if (m.type === "hello") {
        post({ type: "ready", fullscreen: !!document.fullscreenElement, screen: screens[chosen]?.label ?? "this screen",
          float16: !!r.stage.current?.renderer?.info.float16 });
      } else if (m.type === "response") {
        r.responses.pushEpoch(m.key, m.epochMs);
      } else if (m.type === "abort") {
        r.abort();
      } else if (m.type === "run") {
        const s = await api.session(sessionId);
        setSession(s);
        const test = s.tests.find((t) => t.id === m.testId);
        if (!test) return post({ type: "error", testId: m.testId, message: "unknown test" });
        setMessage(`${testTitle(test)}\n${EYE_INSTRUCTION[test.eye] ?? ""}`);
        await new Promise((res) => setTimeout(res, 2500));
        setMessage("");
        const done = await runRef.current(test,
          (p) => post({ type: "progress", testId: test.id, ...p }),
          (st) => post({ type: "sink", ...st }));
        setMessage("Waiting for the operator…");
        if (done) post({ type: "test-done", testId: test.id, status: done.status });
        else post({ type: "error", testId: test.id, message: "test failed; see the console" });
      }
    };
    return () => c.close();
  }, [sessionId, screens, chosen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chan.current?.postMessage({ type: "ready", fullscreen, screen: screens[chosen]?.label ?? "this screen",
      float16: !!r.stage.current?.renderer?.info.float16 } satisfies DisplayToConsole);
  }, [fullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (r.lastError) chan.current?.postMessage({ type: "error", message: r.lastError } satisfies DisplayToConsole);
  }, [r.lastError]);

  const findScreens = async () => {
    const d = await screenDetails();
    if (!d) return setScreens([]);
    setScreens(d.screens.map((s, i) => ({ label: `${(s as { label?: string }).label || `Screen ${i + 1}`} (${s.width}×${s.height})`, screen: s })));
  };

  const goFullscreen = async () => {
    await r.stage.current?.enterFullscreen(screens[chosen]?.screen);
  };

  const bgCode = 1.0;
  useEffect(() => {
    if (r.running) return; // the presenter owns the canvas during a test
    const id = requestAnimationFrame(() => r.stage.current?.renderer?.draw({ show: false, orientation: "right", strokePx: 1, backgroundCode: bgCode, letterCode: bgCode, dither: 0 }));
    return () => cancelAnimationFrame(id);
  });

  return (
    <Stage ref={r.stage}>
      {!r.running && (
        <div className="stage-overlay">
          {!fullscreen ? (
            <div className="panel">
              <h1>Patient display</h1>
              {r.stationError && <p className="error">{r.stationError}</p>}
              {r.geometryWarning && <p className="warn small">{r.geometryWarning}</p>}
              <p>Move this window to the test monitor, or choose it below, then go fullscreen.</p>
              <div className="row">
                <button className="secondary" onClick={() => void findScreens()}>Find screens</button>
                {screens.length > 0 && (
                  <select value={chosen} onChange={(e) => setChosen(Number(e.target.value))}>
                    {screens.map((s, i) => <option key={i} value={i}>{s.label}</option>)}
                  </select>
                )}
                <button onClick={() => void goFullscreen()} disabled={!r.station}>Go fullscreen</button>
              </div>
            </div>
          ) : (
            <div style={{ whiteSpace: "pre-line", fontSize: "1.6rem", textAlign: "center", color: "#555" }}>{message}</div>
          )}
        </div>
      )}
    </Stage>
  );
}
